import { useEffect, useRef, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import {
  Shield, Globe, Database, Network, Search, Camera, Twitter, Users,
  Building2, Target, Scale, Newspaper, Briefcase, Banknote, ChevronRight,
  ArrowRight, Check, Landmark, Clock, Zap, TrendingUp, FileText,
  Lock, AlertTriangle, Eye
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import CookieConsent from '@/components/CookieConsent'
import { SEOHead } from '@/components/SEOHead'

type IndustryModule = { name: string; icon: React.ElementType; desc: string }
type IndustryUseCase = { title: string; scenario: string }
type IndustryStat = { value: string; label: string; numeric?: number; suffix?: string }
type IndustryWorkflowStep = { step: number; title: string; desc: string; icon: React.ElementType }
type IndustryBenchmark = { label: string; pct: number; note: string }

type IndustryData = {
  slug: string
  name: string
  icon: React.ElementType
  freeAccess: boolean
  tagline: string
  description: string
  relevantModules: IndustryModule[]
  useCases: IndustryUseCase[]
  capabilities: string[]
  ctaText: string
  ctaLink: string
  accentColor: string
  stats: IndustryStat[]
  workflow: IndustryWorkflowStep[]
  benchmarks: IndustryBenchmark[]
}

const industries: IndustryData[] = [
  {
    slug: 'government',
    name: 'Government & Defense',
    icon: Building2,
    freeAccess: false,
    accentColor: '#3b82f6',
    tagline: 'Strategic OSINT for national security and public sector agencies',
    description: 'Government agencies and defense organizations require intelligence tools that operate at scale, with precision, and without latency. Sycek was built by operators who understand active threat environments — drawing on 6+ years building and running live cyber intelligence platforms.',
    stats: [
      { value: '14+', label: 'Intelligence domains', numeric: 14 },
      { value: '190+', label: 'Countries covered', numeric: 190 },
      { value: '<30s', label: 'Alert latency', numeric: 30 },
      { value: '6+', label: 'Years operational data', numeric: 6 },
    ],
    workflow: [
      { step: 1, title: 'Signal Collection', desc: 'Ingest from 14+ domains: cyber, social, geospatial, maritime, aviation', icon: Network },
      { step: 2, title: 'Multi-Domain Correlation', desc: 'Automated cross-domain signal correlation with configurable rulesets', icon: Globe },
      { step: 3, title: 'Escalation Scoring', desc: 'Real-time severity scoring with country instability indices', icon: TrendingUp },
      { step: 4, title: 'Alert & Dispatch', desc: 'Push alerts via WebSocket dashboard, API webhook, or email', icon: AlertTriangle },
      { step: 5, title: 'Intelligence Brief', desc: 'Auto-generated PDF briefs with source citations and timeline', icon: FileText },
    ],
    benchmarks: [
      { label: 'Reduction in manual signal triage time', pct: 78, note: 'vs. analyst-only workflows' },
      { label: 'Coverage vs. single-source feeds', pct: 94, note: 'across cyber + geopolitical + social domains' },
      { label: 'Faster situational awareness during crises', pct: 65, note: 'vs. traditional OSINT stacks' },
    ],
    relevantModules: [
      { name: 'Social Stream Intelligence', icon: Network, desc: 'Monitor foreign influence operations across 8+ platforms in real time' },
      { name: 'GEOINT', icon: Globe, desc: 'Satellite imagery, AOI monitoring, and change detection for sensitive sites' },
      { name: 'Sycek Profiler', icon: Users, desc: 'Deep-dive identity correlation for persons of interest' },
      { name: 'Live Monitor', icon: Target, desc: 'Global threat visualization with real-time escalation scoring' },
    ],
    useCases: [
      { title: 'Foreign Influence Operations', scenario: 'Detect and map coordinated inauthentic behavior campaigns targeting domestic narratives across social platforms.' },
      { title: 'Facility Monitoring', scenario: 'Track construction, movement, or activity changes at sensitive sites using historical satellite imagery and AOI alerts.' },
      { title: 'Threat Actor Attribution', scenario: 'Attribute cyber attacks to state-sponsored groups using breach data, IOCs, and dark web intelligence.' },
      { title: 'Crisis Situational Awareness', scenario: 'Get real-time situational awareness during geopolitical crises with multi-domain correlation across cyber, social, and geographic signals.' },
    ],
    capabilities: [
      'Multi-source signal correlation across cyber, geopolitical, and social domains',
      'Real-time monitoring with WebSocket-powered live dashboards',
      'Historical satellite imagery comparison with automated change detection',
      'Secure API with enterprise SSO and full audit logging',
      'STIX/TAXII export for SIEM and threat intelligence platform integration',
      'Air and maritime tracking for logistics and movement intelligence',
    ],
    ctaText: 'Request a Briefing',
    ctaLink: '/register',
  },
  {
    slug: 'law-enforcement',
    name: 'Law Enforcement',
    icon: Shield,
    freeAccess: false,
    accentColor: '#22c55e',
    tagline: 'Digital forensics and investigation tools for police and cybercrime units',
    description: 'Law enforcement agencies trust Sycek for digital investigations, cybercrime attribution, and suspect profiling. Our platform is purpose-built for investigators who need results fast — combining identity correlation, breach intelligence, and geospatial analysis in a single workflow.',
    stats: [
      { value: '40B+', label: 'Breach records indexed', numeric: 40 },
      { value: '8+', label: 'Platforms linked per identity', numeric: 8 },
      { value: '<15m', label: 'Avg. first result', numeric: 15 },
      { value: '100%', label: 'Audit trail compliance', numeric: 100 },
    ],
    workflow: [
      { step: 1, title: 'Seed Identifier', desc: 'Start from any email, phone, username, face photo, or name', icon: Search },
      { step: 2, title: 'Identity Expansion', desc: 'Correlate across breach records, social platforms, and dark web sources', icon: Network },
      { step: 3, title: 'Profile Construction', desc: 'Build a complete digital footprint — locations, associates, accounts', icon: Users },
      { step: 4, title: 'Evidence Preservation', desc: 'Capture timestamps, source URLs, and raw data with tamper-evident logs', icon: Lock },
      { step: 5, title: 'Case Report', desc: 'Export court-ready PDF with full source citations and audit trail', icon: FileText },
    ],
    benchmarks: [
      { label: 'Faster digital profiling vs. manual OSINT', pct: 82, note: 'from seed identifier to full profile' },
      { label: 'Tools replaced by Sycek in investigator workflows', pct: 89, note: '9+ tools consolidated into one platform' },
      { label: 'Reduction in report documentation time', pct: 71, note: 'auto-generated PDF replaces manual write-up' },
    ],
    relevantModules: [
      { name: 'Sycek Profiler', icon: Search, desc: 'Full identity profile from any email, phone, username, or name' },
      { name: 'BreachINT', icon: Database, desc: 'Credential and breach exposure for investigative leads' },
      { name: 'Facial Recognition', icon: Camera, desc: 'Reverse face search across billions of social media images' },
      { name: 'Identity Correlation', icon: Users, desc: 'Link aliases, pseudonyms, and accounts across platforms' },
    ],
    useCases: [
      { title: 'Suspect Digital Profiling', scenario: 'Build a complete digital footprint from a phone number or email — linked accounts, breach history, social profiles, and location signals.' },
      { title: 'Cybercrime Investigation', scenario: 'Trace ransomware actors, fraud rings, and cybercriminals through leaked credentials and infrastructure pivots.' },
      { title: 'Missing Persons', scenario: 'Locate individuals through cross-platform social media correlation, facial recognition, and last-known activity analysis.' },
      { title: 'Counter-Terrorism', scenario: 'Monitor extremist content propagation on Telegram, forums, and social platforms with AI threat scoring and network mapping.' },
    ],
    capabilities: [
      'Full digital identity reconstruction from a single identifier',
      'Court-admissible investigation workflow with tamper-proof audit trails',
      'Facial recognition matching across VK, TikTok, and social platforms',
      'Dark web and breach credential lookups with 40B+ records',
      'Telegram channel monitoring and actor network mapping',
    ],
    ctaText: 'Request Platform Access',
    ctaLink: '/register',
  },
  {
    slug: 'financial-services',
    name: 'Financial Services & Insurance',
    icon: Landmark,
    freeAccess: false,
    accentColor: '#f59e0b',
    tagline: 'Fraud detection, counterparty risk, and financial crime intelligence',
    description: 'Financial institutions face an expanding attack surface — from account takeover and identity fraud to complex financial crime networks. Sycek delivers real-time intelligence that integrates directly into your risk and compliance workflows via API.',
    stats: [
      { value: '190+', label: 'Jurisdictions screened', numeric: 190 },
      { value: '40B+', label: 'Exposed credentials', numeric: 40 },
      { value: '<60s', label: 'Onboarding screening', numeric: 60 },
      { value: '98%', label: 'UBO match accuracy', numeric: 98 },
    ],
    workflow: [
      { step: 1, title: 'Entity Input', desc: 'Submit customer, company, or transaction identifier via UI or API', icon: Database },
      { step: 2, title: 'Sanctions & PEP Check', desc: 'Screen against 190+ jurisdiction watchlists in real time', icon: Shield },
      { step: 3, title: 'Breach Exposure', desc: 'Check if credentials appear in 40B+ breach records', icon: AlertTriangle },
      { step: 4, title: 'Network Mapping', desc: 'Visualize corporate structure, UBOs, and associated entities', icon: Network },
      { step: 5, title: 'Risk Score & Report', desc: 'Automated risk scoring with PDF documentation for compliance', icon: FileText },
    ],
    benchmarks: [
      { label: 'Faster client onboarding screening', pct: 91, note: 'vs. manual PEP/sanctions workflow' },
      { label: 'More UBO layers traced vs. commercial registries alone', pct: 73, note: 'open-source supplementation' },
      { label: 'Reduction in false positives with combined breach + PEP signals', pct: 67, note: 'dual-signal approach' },
    ],
    relevantModules: [
      { name: 'Sycek Profiler', icon: Users, desc: 'PEP screening, sanctions checks, and counterparty risk profiling' },
      { name: 'BreachINT', icon: Database, desc: 'Credential exposure detection and breach history for customers' },
      { name: 'Social Stream', icon: Network, desc: 'Monitor brand reputation threats and financial misinformation' },
      { name: 'Identity Correlation', icon: Search, desc: 'Link corporate entities, beneficial owners, and shell structures' },
    ],
    useCases: [
      { title: 'Account Fraud Prevention', scenario: 'Detect compromised credentials before account takeover occurs using real-time breach monitoring and alerting.' },
      { title: 'Counterparty Due Diligence', scenario: 'Screen new clients against PEP lists, sanctions, breach history, and adverse media in seconds at onboarding.' },
      { title: 'Insurance Fraud Investigation', scenario: 'Verify claimant identity, cross-reference social media activity, and detect fabricated incidents with open-source evidence.' },
      { title: 'Beneficial Ownership Mapping', scenario: 'Trace corporate structures, shell companies, and ultimate beneficial owners through layered open-source data.' },
    ],
    capabilities: [
      'Real-time PEP, sanctions, and watchlist screening across 190+ countries',
      'Breach database with 40B+ exposed credentials for account monitoring',
      'Corporate entity mapping and beneficial ownership discovery',
      'API-first architecture for integration with core banking and KYC systems',
      'Automated risk scoring with configurable alert thresholds',
      'PDF report generation for compliance and audit documentation',
    ],
    ctaText: 'Start Free Trial',
    ctaLink: '/register',
  },
  {
    slug: 'cybersecurity',
    name: 'Cybersecurity Teams & SOCs',
    icon: Target,
    freeAccess: false,
    accentColor: '#ef4444',
    tagline: 'Threat intelligence enrichment and IOC pivoting for security operations',
    description: 'SOC teams and threat intelligence analysts rely on Sycek for rapid IOC enrichment, threat actor profiling, and dark web monitoring. Our cyber intelligence platform is backed by 6+ years of operational threat intelligence data and infrastructure built by practitioners.',
    stats: [
      { value: '70+', label: 'Ransomware groups tracked', numeric: 70 },
      { value: '40B+', label: 'IOCs indexed', numeric: 40 },
      { value: '100ms', label: 'Enrichment response', numeric: 100 },
      { value: '6+', label: 'Years threat actor data', numeric: 6 },
    ],
    workflow: [
      { step: 1, title: 'IOC Intake', desc: 'Submit IP, domain, hash, or actor name via UI, API, or MCP agent', icon: Database },
      { step: 2, title: 'Multi-Source Enrichment', desc: 'Correlate against breach records, CVE data, and ransomware feeds', icon: Network },
      { step: 3, title: 'Actor Attribution', desc: 'Link IOCs to known threat groups using campaign and TTPs database', icon: Target },
      { step: 4, title: 'Threat Scoring', desc: 'Automated severity scoring with CVSS integration and context', icon: TrendingUp },
      { step: 5, title: 'SIEM Export', desc: 'Push enriched indicators as STIX 2.1 to your SIEM or SOAR', icon: Zap },
    ],
    benchmarks: [
      { label: 'Reduction in mean time to enrich an IOC', pct: 84, note: 'sub-100ms API vs. multi-tool manual lookup' },
      { label: 'More context per indicator vs. single feed', pct: 76, note: 'breach + actor + infra correlation' },
      { label: 'Faster threat actor attribution', pct: 69, note: '6+ years of operational threat actor data' },
    ],
    relevantModules: [
      { name: 'BreachINT', icon: Database, desc: 'Dark web breach monitoring and exposed credential intelligence' },
      { name: 'Live Monitor', icon: Target, desc: 'Real-time CVE tracking, ransomware activity, and IOC monitoring' },
      { name: 'Sycek Profiler', icon: Users, desc: 'Threat actor profiling and attribution intelligence' },
      { name: 'MCP Integration', icon: Network, desc: 'AI-native intelligence via Claude, Cursor, or custom LLM agents' },
    ],
    useCases: [
      { title: 'IOC Enrichment', scenario: 'Pivot on an IP, domain, or file hash to build a complete threat picture — linked actors, campaigns, and associated infrastructure.' },
      { title: 'Ransomware Monitoring', scenario: 'Track 70+ ransomware groups, their victims, and leak site activity in real time with automated escalation alerts.' },
      { title: 'Dark Web Monitoring', scenario: 'Monitor your organization\'s credentials, data, and brand mentions across dark web forums and breach repositories.' },
      { title: 'Threat Hunting', scenario: 'Use GEOINT and social intelligence to attribute malicious activity back to physical infrastructure and real-world personas.' },
    ],
    capabilities: [
      'Sycek proprietary threat intelligence network — 6+ years of operational data',
      'Real-time ransomware group tracking across 70+ active threat actors',
      'CISA KEV integration, CVE monitoring, and exploit tracking',
      'STIX 2.1 export and MISP-compatible indicator formats',
      'MCP server for AI-assisted threat analysis with Claude and Cursor',
      'REST API with SDK support for SOAR and SIEM integration',
    ],
    ctaText: 'Get API Access',
    ctaLink: '/register',
  },
  {
    slug: 'legal',
    name: 'Legal & Compliance',
    icon: Scale,
    freeAccess: false,
    accentColor: '#8b5cf6',
    tagline: 'Digital evidence gathering and due diligence for legal professionals',
    description: 'Law firms and compliance teams use Sycek to conduct digital due diligence, gather open-source evidence for litigation, and screen counterparties for legal risk. Our audit trails and PDF reporting are designed for evidentiary standards that legal work demands.',
    stats: [
      { value: '190+', label: 'Jurisdictions covered', numeric: 190 },
      { value: '50+', label: 'Source languages', numeric: 50 },
      { value: '<2m', label: 'PDF report generation', numeric: 2 },
      { value: '100%', label: 'Tamper-evident audit trail', numeric: 100 },
    ],
    workflow: [
      { step: 1, title: 'Subject Identification', desc: 'Input individual, entity, or digital identifier to begin investigation', icon: Search },
      { step: 2, title: 'Adverse Media Scan', desc: 'Search 50+ language sources for litigation, regulatory, and reputational risk', icon: Eye },
      { step: 3, title: 'Sanctions & PEP Check', desc: 'Real-time screening across 190+ jurisdiction watchlists', icon: Shield },
      { step: 4, title: 'Evidence Collection', desc: 'Preserve source materials with metadata, timestamps, and URLs', icon: Database },
      { step: 5, title: 'Documented Report', desc: 'Court-ready PDF with numbered findings, sources, and investigator notes', icon: FileText },
    ],
    benchmarks: [
      { label: 'Faster due diligence vs. manual research', pct: 79, note: 'from intake to final report' },
      { label: 'More sources covered vs. single-language research', pct: 88, note: '50+ languages monitored' },
      { label: 'Reduction in report compilation time', pct: 74, note: 'auto-generated PDF with citations' },
    ],
    relevantModules: [
      { name: 'Sycek Profiler', icon: Users, desc: 'Comprehensive background intelligence on individuals and entities' },
      { name: 'BreachINT', icon: Database, desc: 'Data exposure history for evidentiary and compliance purposes' },
      { name: 'Identity Correlation', icon: Search, desc: 'Link entities, aliases, and digital identities with documented trails' },
      { name: 'Social Stream', icon: Network, desc: 'Preserve and analyze social media evidence with timestamps' },
    ],
    useCases: [
      { title: 'Litigation Support', scenario: 'Gather and document open-source digital evidence with complete audit trails suitable for court proceedings.' },
      { title: 'Corporate Due Diligence', scenario: 'Screen acquisition targets, executives, and partners for adverse media, sanctions, and undisclosed risk profiles.' },
      { title: 'Intellectual Property Investigation', scenario: 'Track IP theft, trademark infringement, and counterfeit operations across digital channels worldwide.' },
      { title: 'Witness & Expert Verification', scenario: 'Independently verify credentials, backgrounds, and public statements of witnesses or opposing expert witnesses.' },
    ],
    capabilities: [
      'Full investigation audit trail with tamper-evident documentation',
      'PDF report generation with timestamps and source citations',
      'PEP and sanctions screening across 190+ jurisdictions',
      'Social media evidence preservation with metadata retention',
      'Corporate structure and beneficial ownership discovery',
      'Adverse media monitoring across global news and media sources',
    ],
    ctaText: 'Start Due Diligence',
    ctaLink: '/register',
  },
  {
    slug: 'journalism',
    name: 'Journalism & Investigative Media',
    icon: Newspaper,
    freeAccess: false,
    accentColor: '#06b6d4',
    tagline: 'Source verification and disinformation investigation for journalists',
    description: 'Investigative journalists and media organizations use Sycek to verify sources, map disinformation campaigns, and build evidence trails for high-impact reporting. Our platform provides the same intelligence-grade tools used by security professionals — designed for the demands of investigative journalism.',
    stats: [
      { value: '8+', label: 'Social platforms monitored', numeric: 8 },
      { value: '3m', label: 'Avg. source verification', numeric: 3 },
      { value: '100+', label: 'Countries indexed', numeric: 100 },
      { value: '1', label: 'Platform — zero tool switching', numeric: 1 },
    ],
    workflow: [
      { step: 1, title: 'Source Intake', desc: 'Input identity, claim, image, or social account for verification', icon: Search },
      { step: 2, title: 'Background Check', desc: 'Cross-reference identity against breach records and social platforms', icon: Database },
      { step: 3, title: 'Network Mapping', desc: 'Identify affiliated accounts, organizations, and influence connections', icon: Network },
      { step: 4, title: 'Geolocation Verify', desc: 'Confirm image or video provenance using GEOINT satellite comparison', icon: Globe },
      { step: 5, title: 'Evidence Package', desc: 'Export timestamped evidence bundle ready for editorial review', icon: FileText },
    ],
    benchmarks: [
      { label: 'Faster source verification vs. manual methods', pct: 77, note: 'avg. 3 minutes vs. 20+ minutes manually' },
      { label: 'More social signals captured per investigation', pct: 85, note: '8+ platforms in one query' },
      { label: 'Reduction in tool-switching overhead', pct: 92, note: 'single platform replaces 9+ tools' },
    ],
    relevantModules: [
      { name: 'Sycek Profiler', icon: Users, desc: 'Source verification and identity background checks' },
      { name: 'X Investigation', icon: Twitter, desc: 'Deep-dive investigation of Twitter accounts and influence campaigns' },
      { name: 'Social Stream', icon: Network, desc: 'Track disinformation narratives across 8+ platforms in real time' },
      { name: 'GEOINT', icon: Globe, desc: 'Satellite imagery for conflict verification and open-source geolocation' },
    ],
    useCases: [
      { title: 'Source Verification', scenario: 'Verify source identities, backgrounds, and potential conflicts of interest before going to publication.' },
      { title: 'Disinformation Investigation', scenario: 'Trace the origin and spread of false narratives, coordinated inauthentic campaigns, and bot networks.' },
      { title: 'Conflict Zone Verification', scenario: 'Verify the location and authenticity of images and videos from conflict zones using satellite imagery and geolocation.' },
      { title: 'Corporate Accountability', scenario: 'Investigate corporate structures, executive backgrounds, and financial relationships through open-source data.' },
    ],
    capabilities: [
      'Source identity verification from minimal digital footprint',
      'Social media network analysis, influence mapping, and bot detection',
      'Satellite imagery for event verification and open-source geolocation',
      'Historical timeline reconstruction of events and information campaigns',
      'Secure investigation environment with user privacy protections',
    ],
    ctaText: 'Get Started',
    ctaLink: '/register',
  },
  {
    slug: 'corporate-risk',
    name: 'Corporate Risk & Enterprise',
    icon: Briefcase,
    freeAccess: false,
    accentColor: '#f97316',
    tagline: 'Executive protection, third-party risk, and insider threat monitoring',
    description: 'Enterprise security and risk teams use Sycek to protect leadership, monitor third-party vendors, and detect insider threats before they escalate. Our platform integrates with existing GRC and security toolchains via API, fitting into your existing risk management infrastructure.',
    stats: [
      { value: '24/7', label: 'Continuous monitoring', numeric: 24 },
      { value: '40B+', label: 'Credential records', numeric: 40 },
      { value: '190+', label: 'Countries geopolitical coverage', numeric: 190 },
      { value: '<5m', label: 'Executive risk brief', numeric: 5 },
    ],
    workflow: [
      { step: 1, title: 'Asset Registration', desc: 'Register executives, domains, and third-party vendors for monitoring', icon: Users },
      { step: 2, title: 'Continuous Watch', desc: 'Real-time alerts across breach, social, geopolitical, and dark web signals', icon: Eye },
      { step: 3, title: 'Threat Correlation', desc: 'Link external threat signals to internal employee and vendor data', icon: Network },
      { step: 4, title: 'Risk Scoring', desc: 'Automated risk score changes trigger configurable alert policies', icon: TrendingUp },
      { step: 5, title: 'Integration & Action', desc: 'Push to GRC, SIEM, or Slack via webhook — close the loop fast', icon: Zap },
    ],
    benchmarks: [
      { label: 'Earlier detection of executive exposure threats', pct: 81, note: 'continuous monitoring vs. periodic checks' },
      { label: 'More third-party risks surfaced at onboarding', pct: 73, note: 'OSINT vs. questionnaire-only EDD' },
      { label: 'Reduction in time to brief leadership on incidents', pct: 86, note: 'automated brief vs. manual report' },
    ],
    relevantModules: [
      { name: 'Sycek Profiler', icon: Users, desc: 'Executive exposure monitoring and threat actor profiling' },
      { name: 'Social Stream', icon: Network, desc: 'Brand monitoring, reputation threats, and executive mention alerts' },
      { name: 'BreachINT', icon: Database, desc: 'Employee credential monitoring and supply chain breach alerts' },
      { name: 'Live Monitor', icon: Target, desc: 'Real-time geopolitical and cyber threat monitoring for global operations' },
    ],
    useCases: [
      { title: 'Executive Protection', scenario: 'Monitor public mentions, threat actor chatter, and location-linked data around C-suite executives and board members.' },
      { title: 'Third-Party Risk', scenario: 'Screen vendors, partners, and suppliers for breach history, sanctions exposure, and adverse media continuously.' },
      { title: 'Insider Threat Detection', scenario: 'Correlate employee digital activity patterns with external threat indicators to surface insider risk signals.' },
      { title: 'M&A Due Diligence', scenario: 'Rapidly assess acquisition targets for cyber exposure, reputational risk, and undisclosed liabilities before close.' },
    ],
    capabilities: [
      'Executive digital footprint monitoring with real-time alert policies',
      'Third-party breach monitoring across the full supply chain',
      'Employee credential exposure detection and dark web monitoring',
      'Geopolitical risk monitoring for international business operations',
      'REST API and webhooks for GRC platform and SIEM integration',
      'Role-based access control for enterprise team and analyst management',
    ],
    ctaText: 'Book a Demo',
    ctaLink: '/register',
  },
  {
    slug: 'aml',
    name: 'Anti-Money Laundering',
    icon: Banknote,
    freeAccess: false,
    accentColor: '#10b981',
    tagline: 'Beneficial ownership discovery, network mapping, and PEP screening',
    description: 'AML compliance teams rely on Sycek to augment traditional KYC workflows with open-source intelligence. Quickly map corporate structures, trace transaction networks, and identify beneficial owners that traditional commercial databases miss.',
    stats: [
      { value: '190+', label: 'PEP/sanctions jurisdictions', numeric: 190 },
      { value: '40B+', label: 'Breach records correlated', numeric: 40 },
      { value: '<1m', label: 'Enhanced screening time', numeric: 1 },
      { value: '6', label: 'UBO tracing depth (layers)', numeric: 6 },
    ],
    workflow: [
      { step: 1, title: 'Subject Input', desc: 'Enter individual or entity name, ID, or registration number', icon: Search },
      { step: 2, title: 'PEP & Sanctions', desc: 'Real-time screening across 190+ jurisdiction watchlists', icon: Shield },
      { step: 3, title: 'UBO Discovery', desc: 'Trace through corporate layers to surface ultimate beneficial owners', icon: Network },
      { step: 4, title: 'Adverse Media', desc: 'Scan 50+ language sources for financial crime and regulatory news', icon: Eye },
      { step: 5, title: 'EDD Report', desc: 'Comprehensive Enhanced Due Diligence report with risk score and sources', icon: FileText },
    ],
    benchmarks: [
      { label: 'Faster enhanced due diligence workflow', pct: 88, note: '<1 minute vs. 30+ minutes manually' },
      { label: 'More UBO layers traced vs. registry-only checks', pct: 71, note: 'open-source augmentation' },
      { label: 'More typologies detected with dark web + breach overlay', pct: 64, note: 'combined intelligence approach' },
    ],
    relevantModules: [
      { name: 'Sycek Profiler', icon: Users, desc: 'PEP/sanctions screening, adverse media, and corporate entity lookup' },
      { name: 'Identity Correlation', icon: Search, desc: 'Link individuals across corporate structures and digital identities' },
      { name: 'BreachINT', icon: Database, desc: 'Detect financial account exposures and compromised credentials' },
      { name: 'Social Stream', icon: Network, desc: 'Monitor for suspicious activity discussions and mule recruitment' },
    ],
    useCases: [
      { title: 'Beneficial Ownership Tracing', scenario: 'Trace ultimate beneficial owners through multi-layer corporate structures across multiple jurisdictions.' },
      { title: 'Enhanced Due Diligence', scenario: 'Deep-profile high-risk customers with adverse media, PEP status, sanctions exposure, and digital footprint analysis.' },
      { title: 'Transaction Network Mapping', scenario: 'Map relationships between entities in complex transaction networks to surface hidden connections and risk.' },
      { title: 'Typology Research', scenario: 'Monitor dark web forums and criminal platforms for emerging money laundering typologies and methods.' },
    ],
    capabilities: [
      'PEP screening across 190+ countries with continuous updates',
      'Corporate structure visualization and UBO tracing through layers',
      'Adverse media monitoring in 50+ languages across global sources',
      'API integration with existing KYC and AML compliance platforms',
      'Automated risk scoring with full audit documentation trail',
      'Crypto wallet address intelligence and on-chain transaction tracing',
    ],
    ctaText: 'Start AML Screening',
    ctaLink: '/register',
  },
  {
    slug: 'private-investigators',
    name: 'Private Investigators',
    icon: Search,
    freeAccess: false,
    accentColor: '#a855f7',
    tagline: 'Full digital footprint analysis and identity intelligence for professional investigators',
    description: 'Private investigators trust Sycek for accurate, fast, and legally-compliant digital profiling. From skip tracing to due diligence investigations, our platform delivers the intelligence needed to close cases faster with documented, defensible evidence trails.',
    stats: [
      { value: '40B+', label: 'Breach records searchable', numeric: 40 },
      { value: '8+', label: 'Platforms per identity', numeric: 8 },
      { value: '<15m', label: 'Avg. locate result', numeric: 15 },
      { value: '1', label: 'Identifier → full profile', numeric: 1 },
    ],
    workflow: [
      { step: 1, title: 'Start Identifier', desc: 'Input email, phone, username, name, or face photo', icon: Search },
      { step: 2, title: 'Breach Pivot', desc: 'Extract linked accounts, passwords, and contact info from 40B+ records', icon: Database },
      { step: 3, title: 'Social Correlation', desc: 'Link all platforms, aliases, and associated accounts automatically', icon: Network },
      { step: 4, title: 'Location Signals', desc: 'Extract IP history, location metadata, and check-in patterns', icon: Globe },
      { step: 5, title: 'Case Report', desc: 'PDF case report with all findings, sources, and evidence chain', icon: FileText },
    ],
    benchmarks: [
      { label: 'Faster skip tracing vs. legacy PI databases', pct: 80, note: 'real-time cross-platform correlation' },
      { label: 'Higher identity match rate on partial identifiers', pct: 87, note: 'AI-assisted alias linking' },
      { label: 'Reduction in case report preparation time', pct: 75, note: 'auto-generated PDF case file' },
    ],
    relevantModules: [
      { name: 'Sycek Profiler', icon: Users, desc: 'Complete digital identity reconstruction from any starting identifier' },
      { name: 'Facial Recognition', icon: Camera, desc: 'Identify unknown individuals from photos across social platforms' },
      { name: 'BreachINT', icon: Database, desc: 'Credential and contact information recovery from breach databases' },
      { name: 'Identity Correlation', icon: Search, desc: 'Link aliases, secondary accounts, and digital identities across platforms' },
    ],
    useCases: [
      { title: 'Skip Tracing', scenario: 'Locate individuals using email addresses, phone numbers, usernames, or social handles with comprehensive cross-platform correlation.' },
      { title: 'Background Investigations', scenario: 'Build comprehensive background profiles including social accounts, associates, locations, and historical online activity.' },
      { title: 'Identity Verification', scenario: 'Verify claimed identities against digital footprints, breach records, and social media presence across platforms.' },
      { title: 'Asset Discovery', scenario: 'Identify undisclosed assets and financial interests through corporate entity searches and structured open-source data.' },
    ],
    capabilities: [
      'Search by email, phone, username, full name, or government ID',
      'Phone number to caller ID and linked social account discovery',
      'Facial recognition matching across social platforms from a single photo',
      'Cross-platform identity linking and alias detection',
      'Breach credential lookup for contact information recovery',
      'PDF case report generation with full source citations',
    ],
    ctaText: 'Start Investigating',
    ctaLink: '/register',
  },
]

const ecosystemBadge: Record<string, string> = {
  cybersecurity: 'Sycek proprietary threat intelligence — 6+ years of operational data',
  'law-enforcement': 'Built for investigators — court-ready documentation included',
  government: 'Enterprise-grade platform built by cyber intelligence operators',
  aml: 'Dark web and breach intelligence integrated natively',
}

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true })

  useEffect(() => {
    if (!inView) return
    const duration = 1200
    const steps = 40
    const increment = target / steps
    let current = 0
    const timer = setInterval(() => {
      current = Math.min(current + increment, target)
      setCount(Math.round(current))
      if (current >= target) clearInterval(timer)
    }, duration / steps)
    return () => clearInterval(timer)
  }, [inView, target])

  return <span ref={ref}>{count}{suffix}</span>
}

function BenchmarkBar({ label, pct, note, color, delay = 0 }: {
  label: string; pct: number; note: string; color: string; delay?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true })

  return (
    <div ref={ref} className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-slate-300 text-sm font-medium">{label}</span>
        <span className="text-sm font-bold tabular-nums" style={{ color }}>
          {inView ? <AnimatedCounter target={pct} suffix="%" /> : '0%'}
        </span>
      </div>
      <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={inView ? { width: `${pct}%` } : { width: 0 }}
          transition={{ duration: 1, delay, ease: 'easeOut' }}
        />
      </div>
      <p className="text-slate-600 text-xs">{note}</p>
    </div>
  )
}

export default function IndustryPage() {
  const { slug } = useParams<{ slug: string }>()
  const industry = industries.find(i => i.slug === slug)

  if (!industry) return <Navigate to="/" replace />

  const Icon = industry.icon

  return (
    <>
      <SEOHead
        title={`${industry.name} — Sycek OSINT Intelligence Platform`}
        description={industry.tagline}
        keywords={`OSINT ${industry.name.toLowerCase()}, intelligence platform, ${industry.name.toLowerCase()} investigation, threat intelligence`}
        canonical={`/industries/${industry.slug}`}
      />

      <div className="min-h-screen bg-[#070b0e] text-white">

        {/* Sticky nav */}
        <header className="sticky top-0 z-50 border-b border-white/[0.07] bg-[#070b0e]/95 backdrop-blur-md">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 rounded-lg overflow-hidden">
                <img src="/logo.jpeg" alt="Sycek" className="w-full h-full object-cover" />
              </div>
              <span className="text-white font-semibold text-lg">Sycek</span>
            </Link>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-slate-300">Industries</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white">{industry.name}</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/5">
                  Sign In
                </Button>
              </Link>
              <Link to={industry.ctaLink}>
                <Button
                  size="sm"
                  className="font-semibold text-black"
                  style={{ backgroundColor: industry.accentColor }}
                >
                  {industry.ctaText}
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="relative py-24 overflow-hidden">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `radial-gradient(ellipse at 20% 50%, ${industry.accentColor}40 0%, transparent 60%),
                                radial-gradient(ellipse at 80% 20%, ${industry.accentColor}20 0%, transparent 50%)`
            }}
          />
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              className="max-w-4xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${industry.accentColor}20`, border: `1px solid ${industry.accentColor}40` }}
                >
                  <Icon className="w-6 h-6" style={{ color: industry.accentColor }} />
                </div>
                <Badge
                  className="text-xs font-medium px-3 py-1"
                  style={{ backgroundColor: `${industry.accentColor}20`, color: industry.accentColor, border: `1px solid ${industry.accentColor}40` }}
                >
                  {industry.name}
                </Badge>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold mb-5 leading-tight">
                <span className="text-white">{industry.tagline.split(' ').slice(0, 3).join(' ')}</span>
                <br />
                <span style={{ color: industry.accentColor }}>
                  {industry.tagline.split(' ').slice(3).join(' ')}
                </span>
              </h1>

              <p className="text-xl text-slate-400 leading-relaxed mb-10 max-w-3xl">
                {industry.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={industry.ctaLink}>
                  <Button
                    size="lg"
                    className="font-semibold text-black px-8 py-6 text-base shadow-xl"
                    style={{ backgroundColor: industry.accentColor }}
                  >
                    {industry.ctaText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/use-cases">
                  <Button
                    size="lg"
                    variant="ghost"
                    className="text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 px-8 py-6 text-base"
                  >
                    See Use Cases
                  </Button>
                </Link>
              </div>

              {ecosystemBadge[industry.slug] && (
                <div className="mt-8 inline-flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-2 text-sm text-slate-400">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  {ecosystemBadge[industry.slug]}
                </div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Stats strip */}
        <section className="border-t border-white/[0.06] bg-black/30">
          <div className="container mx-auto px-6 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {industry.stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  className="text-center"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div
                    className="text-3xl md:text-4xl font-bold mb-1 tabular-nums"
                    style={{ color: industry.accentColor }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-slate-500 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Investigation Workflow */}
        <section className="py-20 border-t border-white/[0.06]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: industry.accentColor }}>
                How it works
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                The {industry.name.split(' ')[0]} investigation workflow
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                From first identifier to final report — every step happens inside Sycek.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {industry.workflow.map((step, i) => {
                  const StepIcon = step.icon
                  return (
                    <motion.div
                      key={step.step}
                      className="relative flex flex-col items-center text-center"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.1 }}
                    >
                      {/* connector line */}
                      {i < industry.workflow.length - 1 && (
                        <div
                          className="hidden md:block absolute top-5 left-[calc(50%+24px)] right-0 h-px opacity-20"
                          style={{ backgroundColor: industry.accentColor }}
                        />
                      )}
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 relative z-10"
                        style={{ backgroundColor: `${industry.accentColor}20`, border: `1px solid ${industry.accentColor}40` }}
                      >
                        <StepIcon className="w-5 h-5" style={{ color: industry.accentColor }} />
                      </div>
                      <div
                        className="text-xs font-mono font-bold mb-1"
                        style={{ color: industry.accentColor }}
                      >
                        Step {step.step}
                      </div>
                      <div className="text-white text-sm font-semibold mb-1">{step.title}</div>
                      <div className="text-slate-500 text-xs leading-relaxed">{step.desc}</div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Relevant Modules */}
        <section className="py-20 border-t border-white/[0.06] bg-black/20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Platform capabilities for {industry.name.split(' ')[0]}
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                These Sycek modules are most relevant to your workflows and investigative needs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {industry.relevantModules.map((mod, i) => {
                const ModIcon = mod.icon
                return (
                  <motion.div
                    key={mod.name}
                    className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-6 hover:border-white/15 transition-all duration-200 hover:bg-white/[0.05]"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${industry.accentColor}18`, border: `1px solid ${industry.accentColor}35` }}
                    >
                      <ModIcon className="w-5 h-5" style={{ color: industry.accentColor }} />
                    </div>
                    <h3 className="text-white font-semibold mb-2 text-sm">{mod.name}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{mod.desc}</p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 border-t border-white/[0.06]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Use cases
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                How {industry.name} professionals use Sycek in practice.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
              {industry.useCases.map((uc, i) => (
                <motion.div
                  key={uc.title}
                  className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-7"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div
                    className="text-xs font-mono uppercase tracking-widest mb-3 font-medium"
                    style={{ color: industry.accentColor }}
                  >
                    Use Case {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="text-white font-bold text-lg mb-3">{uc.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{uc.scenario}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/use-cases">
                <Button variant="ghost" className="text-slate-400 hover:text-white border border-white/10 hover:bg-white/5">
                  See all detailed use cases
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Capabilities + Benchmarks */}
        <section className="py-20 border-t border-white/[0.06] bg-black/20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

              {/* Capabilities */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  What's included
                </h2>
                <p className="text-slate-400 text-lg mb-8">
                  Every capability your team needs — available from day one.
                </p>
                <div className="space-y-3">
                  {industry.capabilities.map((cap, i) => (
                    <motion.div
                      key={i}
                      className="flex items-start gap-3 bg-white/[0.03] border border-white/[0.06] rounded-lg px-5 py-4"
                      initial={{ opacity: 0, x: -16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: i * 0.06 }}
                    >
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${industry.accentColor}25` }}
                      >
                        <Check className="w-3 h-3" style={{ color: industry.accentColor }} />
                      </div>
                      <span className="text-slate-300 text-sm leading-relaxed">{cap}</span>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-8">
                  <Link to={industry.ctaLink}>
                    <Button
                      size="lg"
                      className="font-semibold text-black px-8 py-5"
                      style={{ backgroundColor: industry.accentColor }}
                    >
                      {industry.ctaText}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Benchmarks */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5" style={{ color: industry.accentColor }} />
                  <h2 className="text-3xl font-bold text-white">Performance benchmarks</h2>
                </div>
                <p className="text-slate-400 text-base mb-10">
                  Measured against traditional multi-tool OSINT workflows.
                </p>

                <div className="space-y-8">
                  {industry.benchmarks.map((b, i) => (
                    <BenchmarkBar
                      key={b.label}
                      label={b.label}
                      pct={b.pct}
                      note={b.note}
                      color={industry.accentColor}
                      delay={i * 0.15}
                    />
                  ))}
                </div>

                {/* Platform credential tiles */}
                <div className="grid grid-cols-2 gap-3 mt-10">
                  {[
                    { icon: Clock, label: '6+ Years', sub: 'Operational intelligence data' },
                    { icon: Shield, label: 'Audit-Ready', sub: 'Tamper-evident workflows' },
                    { icon: Lock, label: 'RBAC + JWT', sub: 'Enterprise access controls' },
                    { icon: Zap, label: 'API-First', sub: 'Integrate into any stack' },
                  ].map((tile) => {
                    const TileIcon = tile.icon
                    return (
                      <div
                        key={tile.label}
                        className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 flex items-start gap-3"
                      >
                        <TileIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: industry.accentColor }} />
                        <div>
                          <div className="text-white text-sm font-semibold">{tile.label}</div>
                          <div className="text-slate-500 text-xs">{tile.sub}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Credentials */}
        <section className="py-16 border-t border-white/[0.06] bg-black/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <p className="text-slate-500 text-sm uppercase tracking-widest font-mono mb-3">Built by operators</p>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Intelligence infrastructure that's been battle-tested
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  Sycek is built and maintained by CyberSafe — a cyber intelligence company with 6+ years running live threat monitoring and OSINT operations for enterprise clients globally.
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Since 2017', sub: 'Operational history', icon: Clock },
                  { label: '40B+ Records', sub: 'Breach database indexed', icon: Database },
                  { label: '190+ Countries', sub: 'Geopolitical coverage', icon: Globe },
                  { label: 'Enterprise Grade', sub: 'SOC 2 aligned architecture', icon: Shield },
                ].map((item) => {
                  const ItemIcon = item.icon
                  return (
                    <div
                      key={item.label}
                      className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 flex flex-col items-center text-center gap-3"
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${industry.accentColor}18`, border: `1px solid ${industry.accentColor}30` }}
                      >
                        <ItemIcon className="w-5 h-5" style={{ color: industry.accentColor }} />
                      </div>
                      <div>
                        <div className="text-white text-sm font-bold">{item.label}</div>
                        <div className="text-slate-500 text-xs mt-0.5">{item.sub}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 border-t border-white/[0.06]">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Ready to get started?
              </h2>
              <p className="text-slate-400 text-xl mb-10 max-w-2xl mx-auto">
                {`Join ${industry.name.toLowerCase()} professionals using Sycek to move faster on intelligence.`}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to={industry.ctaLink}>
                  <Button
                    size="lg"
                    className="font-semibold text-black px-10 py-6 text-lg shadow-2xl"
                    style={{ backgroundColor: industry.accentColor, boxShadow: `0 20px 60px ${industry.accentColor}40` }}
                  >
                    {industry.ctaText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/use-cases">
                  <Button
                    size="lg"
                    variant="ghost"
                    className="text-slate-300 hover:text-white hover:bg-white/5 border border-white/10 px-8 py-6 text-base"
                  >
                    Explore use cases
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/[0.06] py-10 bg-black/40">
          <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <div className="w-6 h-6 rounded overflow-hidden">
                <img src="/logo.jpeg" alt="Sycek" className="w-full h-full object-cover" />
              </div>
              <span className="text-sm">Sycek Intelligence Platform</span>
            </Link>
            <p className="text-slate-600 text-sm">© 2026 Sycek. All rights reserved.</p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/privacy-policy" className="text-slate-500 hover:text-slate-300 transition-colors">Privacy</Link>
              <Link to="/terms-of-service" className="text-slate-500 hover:text-slate-300 transition-colors">Terms</Link>
              <Link to="/use-cases" className="text-slate-500 hover:text-slate-300 transition-colors">Use Cases</Link>
            </div>
          </div>
        </footer>
      </div>
      <CookieConsent />
    </>
  )
}
